import { useContext } from "react";
import { GlobalIcon, IconNameT } from "get-life-storybook-ts";
import { I18nContext } from "../../../../../../context/i18n.context";

interface QuickActionsRowProps {
  isCancelled: boolean;
  contractURL?: string;
  navigateToRoute: (route: string) => void;
  onCall: () => void;
}

interface Action {
  icon: IconNameT;
  label: string;
  onClick: () => void;
  primary: boolean;
  comingSoon?: boolean;
}

const QuickActionsRow = ({
  isCancelled,
  contractURL,
  navigateToRoute,
  onCall,
}: QuickActionsRowProps) => {
  const {
    state: { translate },
  } = useContext(I18nContext);

  const openContract = () => {
    if (contractURL) window.open(contractURL, "_blank");
  };

  const activeActions: Action[] = [
    {
      icon: "DownloadIcon",
      label: translate("newDashboard.redesign.downloadPolicy"),
      onClick: openContract,
      primary: false,
    },
    {
      icon: "SimpleUserIcon",
      label: translate("newDashboard.redesign.manageBeneficiaries"),
      onClick: () => navigateToRoute("beneficiaries"),
      primary: false,
      comingSoon: true,
    },
    {
      icon: "FileInvoiceIcon",
      label: translate("newDashboard.redesign.viewReceipts"),
      onClick: () => navigateToRoute("billing"),
      primary: false,
    },
    {
      icon: "ShieldChevronIcon",
      label: translate("newDashboard.redesign.expandCoverage"),
      onClick: () => navigateToRoute("upgrade"),
      primary: true,
      comingSoon: true,
    },
  ];

  const cancelledActions: Action[] = [
    {
      icon: "DownloadIcon",
      label: translate("newDashboard.redesign.downloadHistoricPolicy"),
      onClick: openContract,
      primary: false,
    },
    {
      icon: "FileInvoiceIcon",
      label: translate("newDashboard.redesign.viewPastReceipts"),
      onClick: () => navigateToRoute("billing"),
      primary: false,
    },
    {
      icon: "PhoneIcon",
      label: translate("newDashboard.redesign.contactSupport"),
      onClick: onCall,
      primary: false,
    },
    {
      icon: "ArrowGoRightIcon",
      label: translate("newDashboard.redesign.reactivatePolicy"),
      onClick: onCall,
      primary: true,
    },
  ];

  const actions = isCancelled ? cancelledActions : activeActions;

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {actions.map((action, idx) => {
        const disabled = action.comingSoon === true;
        return (
          <button
            key={idx}
            onClick={disabled ? undefined : action.onClick}
            disabled={disabled}
            style={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 12,
              padding: 20,
              borderRadius: 12,
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
              cursor: disabled ? "not-allowed" : "pointer",
              textAlign: "center",
              border: "none",
              transition: "box-shadow 0.15s, transform 0.15s",
              backgroundColor: action.primary ? "#4f46e5" : "#fff",
              color: action.primary ? "#fff" : "#374151",
              opacity: disabled ? 0.55 : 1,
            }}
            onMouseEnter={(e) => {
              if (disabled) return;
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.12)";
              e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              if (disabled) return;
              e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.08)";
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            {disabled && (
              <span
                style={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  backgroundColor: "#fef3c7",
                  color: "#92400e",
                  fontSize: 10,
                  fontWeight: 600,
                  padding: "2px 8px",
                  borderRadius: 20,
                  lineHeight: 1.4,
                  whiteSpace: "nowrap",
                }}
              >
                {translate("newDashboard.redesign.comingSoon")}
              </span>
            )}
            <GlobalIcon
              iconName={action.icon}
              size="XS"
              color={action.primary ? "#ffffff" : "#4f46e5"}
            />
            <span style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3 }}>
              {action.label}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default QuickActionsRow;
